/**
 * Executive Suite — Email Funnel client helpers.
 *
 * Firestore (`execInboxThreads`) is the real-time mirror the UI subscribes to.
 * Mutations (sync, classify, send, label map, thread bodies) go through Cloud
 * Functions which write back to Gmail labels and perform delegated sends.
 * Per-mailbox config lives on `users/{ownerUid}.execInbox`.
 */

import {
  collection, doc, getDoc, onSnapshot, orderBy, query, where, limit,
  setDoc, updateDoc, deleteDoc, Timestamp, deleteField, Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import {
  ExecInboxThread, ExecInboxStage, ExecInboxMailboxConfig, ExecInboxGuardrails,
  ExecInboxAgentLogEntry, DEFAULT_EXEC_INBOX_STAGES, DEFAULT_EXEC_INBOX_GUARDRAILS,
} from '../types';

const CLOUD_FUNCTION_BASE = 'https://us-central1-lctf-projects.cloudfunctions.net';
const THREADS = 'execInboxThreads';
const AGENT_LOG = 'execInboxAgentLog';

async function authedPost<T = any>(fn: string, body: Record<string, unknown>): Promise<T> {
  const u = auth.currentUser;
  const token = u && !u.isAnonymous ? await u.getIdToken() : null;
  const resp = await fetch(`${CLOUD_FUNCTION_BASE}/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || data?.success === false) {
    throw new Error(data?.error || `${fn} failed (${resp.status})`);
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Mapping
// ---------------------------------------------------------------------------

function toDate(v: any): Date {
  if (!v) return new Date(0);
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v === 'number') return new Date(v);
  return new Date(v);
}

function mapThread(id: string, d: any): ExecInboxThread {
  return {
    id,
    mailboxUserId: d.mailboxUserId,
    threadId: d.threadId,
    allowedUids: d.allowedUids || [],
    subject: d.subject || '(no subject)',
    lastFrom: d.lastFrom || '',
    lastFromName: d.lastFromName || '',
    lastFromIsOwner: !!d.lastFromIsOwner,
    snippet: d.snippet || '',
    agentSummary: d.agentSummary
      ? {
          ...d.agentSummary,
          updatedAt: d.agentSummary.updatedAt ? toDate(d.agentSummary.updatedAt) : undefined,
        }
      : undefined,
    participants: d.participants || [],
    counterparts: Array.isArray(d.counterparts) ? d.counterparts : [],
    primaryRecipients: Array.isArray(d.primaryRecipients) ? d.primaryRecipients : [],
    ccRecipients: Array.isArray(d.ccRecipients) ? d.ccRecipients : [],
    lastMessageAt: toDate(d.lastMessageAt),
    messageCount: d.messageCount || 1,
    unread: !!d.unread,
    hasAttachments: !!d.hasAttachments,
    stage: d.stage || 'unsorted',
    gmailLabelIds: d.gmailLabelIds || [],
    assignedTo: d.assignedTo || undefined,
    linkedTo: d.linkedTo || undefined,
    proposedLink: d.proposedLink
      ? { ...d.proposedLink, createdAt: d.proposedLink.createdAt ? toDate(d.proposedLink.createdAt) : undefined }
      : undefined,
    proposedDraft: d.proposedDraft
      ? { ...d.proposedDraft, createdAt: toDate(d.proposedDraft.createdAt) }
      : undefined,
    agent: d.agent
      ? { ...d.agent, lastActionAt: d.agent.lastActionAt ? toDate(d.agent.lastActionAt) : undefined }
      : undefined,
    lastSentBy: d.lastSentBy || undefined,
    cursorSeq: d.cursorSeq || 0,
    lastClassifiedBy: d.lastClassifiedBy || undefined,
    createdAt: toDate(d.createdAt),
    updatedAt: toDate(d.updatedAt),
  };
}

// ---------------------------------------------------------------------------
// Real-time subscriptions
// ---------------------------------------------------------------------------

/** Live mirror of a mailbox's recent threads (newest first). */
export function subscribeExecThreads(
  mailboxUserId: string,
  cb: (threads: ExecInboxThread[]) => void,
  onError?: (e: Error) => void,
  max = 500,
): Unsubscribe {
  const q = query(
    collection(db, THREADS),
    where('mailboxUserId', '==', mailboxUserId),
    orderBy('lastMessageAt', 'desc'),
    limit(max),
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => mapThread(d.id, d.data()))),
    (err) => onError?.(err as Error),
  );
}

export function subscribeAgentLog(
  mailboxUserId: string,
  cb: (entries: (ExecInboxAgentLogEntry & { id: string })[]) => void,
  max = 50,
): Unsubscribe {
  const q = query(
    collection(db, AGENT_LOG),
    where('mailboxUserId', '==', mailboxUserId),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any), createdAt: toDate((d.data() as any).createdAt) }))));
}

// ---------------------------------------------------------------------------
// Mutations (via Cloud Functions → Gmail write-back)
// ---------------------------------------------------------------------------

export interface ExecSyncResult {
  success?: boolean;
  synced: number;
  changed: number;
  scanned: number;
  nextPageToken?: string | null;
}

export function syncExecutiveInbox(
  mailboxUserId: string,
  opts?: { maxResults?: number; q?: string; pageToken?: string },
): Promise<ExecSyncResult> {
  return authedPost('syncExecutiveInbox', { mailboxUserId, ...opts });
}

/** Register or confirm Gmail push watch (no-op when desktop agent owns an active watch). */
export function ensureExecGmailWatch(
  mailboxUserId: string,
  opts?: { force?: boolean },
): Promise<{
  success: boolean;
  skipped?: boolean;
  watchActive?: boolean;
  watchOwner?: string;
  topicName?: string;
  historyId?: string;
  expiration?: string;
  message?: string;
}> {
  return authedPost('ensureExecGmailWatch', { mailboxUserId, force: opts?.force ?? false });
}

/** Paginate through Gmail until all funnel-matching threads are mirrored (or maxPages reached). */
export async function syncAllExecutiveInbox(
  mailboxUserId: string,
  opts?: {
    maxResults?: number;
    maxPages?: number;
    q?: string;
    onProgress?: (p: { totalSynced: number; totalChanged: number; page: number }) => void;
  },
): Promise<{ totalSynced: number; totalChanged: number; pages: number; hasMore: boolean }> {
  const maxPages = opts?.maxPages ?? 100;
  const maxResults = opts?.maxResults ?? 100;
  let pageToken: string | undefined;
  let totalSynced = 0;
  let totalChanged = 0;
  let pages = 0;
  let hasMore = false;

  while (pages < maxPages) {
    const r = await syncExecutiveInbox(mailboxUserId, { maxResults, pageToken, q: opts?.q });
    totalSynced += r.synced ?? 0;
    totalChanged += r.changed ?? 0;
    pages += 1;
    opts?.onProgress?.({ totalSynced, totalChanged, page: pages });
    if (!r.nextPageToken) {
      hasMore = false;
      break;
    }
    pageToken = r.nextPageToken;
    hasMore = true;
  }

  if (pages >= maxPages && pageToken) hasMore = true;

  return { totalSynced, totalChanged, pages, hasMore };
}

export function getExecLabelMap(mailboxUserId: string): Promise<{ labelMap: Record<string, string>; stages: ExecInboxStage[] }> {
  return authedPost('getExecLabelMap', { mailboxUserId });
}

export function classifyExecThread(mailboxUserId: string, threadId: string, stage: string) {
  return authedPost('classifyExecThread', { mailboxUserId, threadId, stage });
}

export function deleteExecThread(mailboxUserId: string, threadId: string) {
  return authedPost('deleteExecThread', { mailboxUserId, threadId });
}

/** Drop a local mirror when Gmail already deleted the thread (orphan cleanup). */
export async function deleteExecThreadMirror(threadDocId: string): Promise<void> {
  if (!threadDocId) throw new Error('Missing thread document id');
  await deleteDoc(doc(db, THREADS, threadDocId));
}

export function restoreExecThread(
  mailboxUserId: string,
  threadId: string,
  snapshot?: Record<string, unknown>,
) {
  return authedPost('restoreExecThread', { mailboxUserId, threadId, snapshot });
}

/** CRM-only mirror fields to preserve across delete → undo. */
export function execThreadUndoSnapshot(thread: ExecInboxThread): Record<string, unknown> {
  return {
    stage: thread.stage,
    linkedTo: thread.linkedTo ?? null,
    assignedTo: thread.assignedTo ?? null,
    lastSentBy: thread.lastSentBy ?? null,
    proposedDraft: thread.proposedDraft
      ? { ...thread.proposedDraft, createdAt: thread.proposedDraft.createdAt?.toISOString?.() || thread.proposedDraft.createdAt }
      : null,
    agent: thread.agent
      ? { ...thread.agent, lastActionAt: thread.agent.lastActionAt?.toISOString?.() || thread.agent.lastActionAt }
      : null,
  };
}

export interface ExecMessage {
  id: string;
  threadId: string;
  from: string;
  fromName: string;
  to: string;
  cc: string;
  subject: string;
  date: string;
  internalDate?: number;
  messageIdHeader: string;
  references: string;
  snippet: string;
  bodyHtml: string;
  bodyText: string;
  unread: boolean;
  hasAttachments: boolean;
  attachments?: { id: string; filename: string; mimeType: string; size: number }[];
}

export interface ExecPushDestination {
  kind: 'lead_document' | 'project_file' | 'ops_comment';
  leadId?: string;
  projectId?: string;
  dealId?: string;
  taskId?: string;
  note?: string;
}

export interface ExecPushResult {
  success: boolean;
  docId: string;
  url: string;
  kind: string;
  name: string;
}

export function getExecThread(mailboxUserId: string, threadId: string): Promise<{ messages: ExecMessage[] }> {
  return authedPost('getExecThread', { mailboxUserId, threadId });
}

/** Mark a thread read (or unread) in Gmail and the Firestore mirror. */
export function markExecThreadRead(mailboxUserId: string, threadId: string, unread = false) {
  return authedPost('markExecThreadRead', { mailboxUserId, threadId, unread });
}

export interface ForwardAttachmentRef {
  messageId: string;
  attachmentId: string;
  filename?: string;
}

export interface SendReplyInput {
  to: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject: string;
  bodyHtml?: string;
  bodyText?: string;
  threadId?: string;
  inReplyTo?: string;
  references?: string;
  moveToStage?: string;
  /** Original-message attachments to include (used when forwarding). */
  forwardAttachments?: ForwardAttachmentRef[];
}

export function sendExecReply(mailboxUserId: string, input: SendReplyInput) {
  return authedPost('sendExecReply', { mailboxUserId, ...input });
}

/** Fetch attachment bytes as a Blob (for preview or save). */
export async function fetchExecAttachmentBlob(
  mailboxUserId: string,
  messageId: string,
  attachmentId: string,
  hints?: { filename?: string; mimeType?: string },
): Promise<Blob> {
  const u = auth.currentUser;
  const token = u && !u.isAnonymous ? await u.getIdToken() : null;
  const resp = await fetch(`${CLOUD_FUNCTION_BASE}/getExecAttachment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      mailboxUserId,
      messageId,
      attachmentId,
      filename: hints?.filename,
      mimeType: hints?.mimeType,
    }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data?.error || `Download failed (${resp.status})`);
  }
  return resp.blob();
}

/** Download attachment bytes (binary blob) for local save. */
export async function downloadExecAttachment(
  mailboxUserId: string,
  messageId: string,
  attachmentId: string,
  filename: string,
): Promise<void> {
  const blob = await fetchExecAttachmentBlob(mailboxUserId, messageId, attachmentId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function pushExecAttachment(
  mailboxUserId: string,
  input: {
    threadId: string;
    messageId: string;
    attachmentId: string;
    filename?: string;
    mimeType?: string;
    destination: ExecPushDestination;
  },
): Promise<ExecPushResult> {
  return authedPost('pushExecAttachment', { mailboxUserId, ...input });
}

// ---------------------------------------------------------------------------
// Draft review (writes directly to the thread mirror)
// ---------------------------------------------------------------------------

export async function setDraftStatus(mailboxUserId: string, threadId: string, status: 'approved' | 'discarded') {
  await updateDoc(doc(db, THREADS, `${mailboxUserId}_${threadId}`), {
    'proposedDraft.status': status,
    updatedAt: Timestamp.now(),
  });
}

export async function clearDraft(mailboxUserId: string, threadId: string) {
  await updateDoc(doc(db, THREADS, `${mailboxUserId}_${threadId}`), {
    proposedDraft: deleteField(),
    updatedAt: Timestamp.now(),
  });
}

export async function assignThread(mailboxUserId: string, threadId: string, assigneeUid: string | null) {
  await updateDoc(doc(db, THREADS, `${mailboxUserId}_${threadId}`), {
    assignedTo: assigneeUid || deleteField(),
    updatedAt: Timestamp.now(),
  });
}

export async function linkThread(
  mailboxUserId: string,
  threadId: string,
  linkedTo: { type: 'lead' | 'deal' | 'project'; id: string; name: string } | null,
) {
  await updateDoc(doc(db, THREADS, `${mailboxUserId}_${threadId}`), {
    linkedTo: linkedTo || deleteField(),
    proposedLink: deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/** Accept the agent's pending link suggestion. */
export async function acceptProposedLink(
  mailboxUserId: string,
  threadId: string,
  target: { type: 'lead' | 'deal' | 'project'; id: string; name: string },
) {
  await updateDoc(doc(db, THREADS, `${mailboxUserId}_${threadId}`), {
    linkedTo: target,
    proposedLink: deleteField(),
    updatedAt: Timestamp.now(),
  });
}

/** Dismiss the agent's pending link suggestion. */
export async function dismissProposedLink(mailboxUserId: string, threadId: string) {
  await updateDoc(doc(db, THREADS, `${mailboxUserId}_${threadId}`), {
    proposedLink: deleteField(),
    updatedAt: Timestamp.now(),
  });
}

// ---------------------------------------------------------------------------
// Mailbox config (users/{ownerUid}.execInbox)
// ---------------------------------------------------------------------------

export function defaultMailboxConfig(): ExecInboxMailboxConfig {
  return {
    enabled: false,
    delegates: [],
    stages: DEFAULT_EXEC_INBOX_STAGES,
    agentEnabled: false,
    autonomyLevel: 0,
    guardrails: DEFAULT_EXEC_INBOX_GUARDRAILS,
  };
}

function mapMailboxConfig(cfg: Partial<ExecInboxMailboxConfig>): ExecInboxMailboxConfig {
  const gw = cfg.gmailWatch;
  return {
    ...defaultMailboxConfig(),
    ...cfg,
    guardrails: { ...DEFAULT_EXEC_INBOX_GUARDRAILS, ...(cfg.guardrails || {}) },
    stages: cfg.stages && cfg.stages.length ? cfg.stages : DEFAULT_EXEC_INBOX_STAGES,
    lastSyncAt: cfg.lastSyncAt ? toDate(cfg.lastSyncAt) : undefined,
    gmailWatch: gw ? {
      ...gw,
      expiration: gw.expiration ? toDate(gw.expiration) : undefined,
      registeredAt: gw.registeredAt ? toDate(gw.registeredAt) : undefined,
      lastNotificationAt: gw.lastNotificationAt ? toDate(gw.lastNotificationAt) : undefined,
    } : undefined,
  };
}

export async function getMailboxConfig(ownerUid: string): Promise<ExecInboxMailboxConfig> {
  const snap = await getDoc(doc(db, 'users', ownerUid));
  const cfg = (snap.data()?.execInbox || {}) as Partial<ExecInboxMailboxConfig>;
  return mapMailboxConfig(cfg);
}

export function subscribeMailboxConfig(ownerUid: string, cb: (cfg: ExecInboxMailboxConfig | null, connected: boolean) => void): Unsubscribe {
  return onSnapshot(doc(db, 'users', ownerUid), (snap) => {
    const data = snap.data();
    if (!data) { cb(null, false); return; }
    const cfg = (data.execInbox || {}) as Partial<ExecInboxMailboxConfig>;
    cb(
      mapMailboxConfig(cfg),
      !!data.gmailConnection?.accessToken,
    );
  });
}

export async function updateMailboxConfig(ownerUid: string, partial: Partial<ExecInboxMailboxConfig>) {
  await setDoc(doc(db, 'users', ownerUid), { execInbox: partial }, { merge: true });
}

export async function updateGuardrails(ownerUid: string, partial: Partial<ExecInboxGuardrails>) {
  const current = await getMailboxConfig(ownerUid);
  await updateMailboxConfig(ownerUid, { guardrails: { ...current.guardrails!, ...partial } });
}

export async function saveStages(ownerUid: string, stages: ExecInboxStage[]) {
  await updateMailboxConfig(ownerUid, { stages });
}

/** Generate a fresh agent API key (returned once; stored on the mailbox config). */
export async function rotateAgentApiKey(ownerUid: string): Promise<string> {
  const key = `exa_${cryptoRandom(40)}`;
  await updateMailboxConfig(ownerUid, { agentApiKey: key });
  return key;
}

function cryptoRandom(len: number): string {
  const bytes = new Uint8Array(len);
  (window.crypto || (window as any).msCrypto).getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, len);
}
